/**
 * Servicio de Notificaciones Nativas del Sistema Operativo.
 * 
 * Wrapper sobre la Web Notifications API del navegador.
 * Las notificaciones aparecen en el centro de notificaciones de Windows
 * y son especialmente útiles cuando el usuario NO tiene la pestaña en foco
 * (ej: está usando PiP o trabajando en otra app).
 * 
 * Decisión de diseño: Solo se envían notificaciones nativas cuando
 * la pestaña NO está visible, para no duplicar con los toasts internos.
 */

type NotificationOptions = {
    title: string
    body: string
    icon?: string
    tag?: string // Previene duplicados con el mismo tag
    force?: boolean // Ignora la verificación de visibilidad (para pruebas)
}

const DEFAULT_ICON = '/favicon.ico'

/**
 * Verifica si el navegador soporta la API de notificaciones
 */
export function isNotificationSupported(): boolean {
    return 'Notification' in window
}

/**
 * Solicita permiso al usuario para enviar notificaciones.
 * Retorna true si el permiso fue concedido.
 */
export async function requestNotificationPermission(): Promise<boolean> {
    if (!isNotificationSupported()) return false

    if (Notification.permission === 'granted') return true
    if (Notification.permission === 'denied') return false

    const permission = await Notification.requestPermission()
    return permission === 'granted'
}

/**
 * Obtiene el estado actual del permiso de notificaciones.
 */
export function getNotificationPermission(): NotificationPermission | 'unsupported' {
    if (!isNotificationSupported()) return 'unsupported'
    return Notification.permission
}

/**
 * Envía una notificación nativa del SO.
 * Solo la envía si:
 * 1. El navegador soporta notificaciones
 * 2. El permiso está concedido
 * 3. La pestaña NO está visible (para no duplicar con toasts)
 */
export function sendDesktopNotification({ title, body, icon, tag, force }: NotificationOptions): void {
    if (!isNotificationSupported()) return
    if (Notification.permission !== 'granted') return

    // Solo enviar si la pestaña NO está en primer plano (salvo que se fuerce)
    if (document.visibilityState === 'visible' && !force) return

    try {
        const notification = new Notification(title, {
            body,
            icon: icon || DEFAULT_ICON,
            tag: tag || undefined,
            silent: true, // No duplicar sonido con el del timer
        })

        // Auto-cerrar tras 8 segundos
        setTimeout(() => notification.close(), 8000)

        // Al hacer clic, enfocar la pestaña de Tempora
        notification.onclick = () => {
            window.focus()
            notification.close()
        }
    } catch (error) {
        // Fallback silencioso - las notificaciones no son críticas
        console.warn('No se pudo enviar notificación nativa:', error)
    }
}
